import { useContext } from 'react';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { Link, useNavigate } from 'react-router-dom';
import AppContext from '../services/context';
import PageTemplate from '../components/PageTemplate';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { trackEvent } from '../lib/analytics';

const PlanCard = ({ title, description, cta, disabled, selected, recommended, onClick, badge, helper }) => (
  <div
    className={clsx(
      'border rounded-2xl p-5 space-y-3 h-full flex flex-col',
      'bg-white/70 border-gray-200 dark:bg-gray-950/20 dark:border-gray-800',
      selected && 'border-greenbook-light-primary dark:border-greenbook-dark-primary',
      recommended && 'bg-greenbook-light-green/20 shadow-sm dark:bg-greenbook-dark-green/30',
      disabled ? 'opacity-70' : 'opacity-100'
    )}
  >
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-semibold">{title}</h3>
      {badge && (
        <span className="px-3 py-1 text-xs rounded-full bg-amber-200 text-amber-900 font-semibold">{badge}</span>
      )}
    </div>
    <p className="text-sm text-gray-700 dark:text-gray-200 flex-1">{description}</p>
    {helper && <p className="text-xs text-gray-600 dark:text-gray-300">{helper}</p>}
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'mt-2 px-4 py-2 rounded-lg font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        disabled
          ? 'bg-gray-200 text-gray-600 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400'
        : selected || recommended
          ? 'cursor-pointer bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900 focus-visible:outline-greenbook-light-primary dark:bg-greenbook-dark-primary dark:text-greenbook-dark-toggle-text dark:hover:bg-greenbook-light-green dark:focus-visible:outline-greenbook-dark-primary'
          : 'cursor-pointer bg-white text-gray-900 border border-gray-300 hover:border-greenbook-light-primary focus-visible:outline-greenbook-light-primary dark:bg-gray-950/30 dark:text-greenbook-dark-text dark:border-gray-700 dark:hover:border-greenbook-dark-primary dark:focus-visible:outline-greenbook-dark-primary'
      )}
    >
      {cta}
    </button>
  </div>
);

PlanCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  cta: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  recommended: PropTypes.bool,
  onClick: PropTypes.func,
  badge: PropTypes.string,
  helper: PropTypes.string,
};

PlanCard.defaultProps = {
  disabled: false,
  selected: false,
  recommended: false,
  onClick: undefined,
  badge: undefined,
  helper: undefined,
};

const Plans = () => {
  const { userState, userActions } = useContext(AppContext);
  const navigate = useNavigate();
  usePageTitle('Planos');
  const isAuthenticated = userState?.isAuthenticated;
  const plan = userState?.currentPlan;

  const handleSelectPlan = (selectedPlan) => {
    trackEvent('plan_selected', {
      plan: selectedPlan,
      source: 'plans_page',
      authenticated: Boolean(isAuthenticated),
      current_plan: plan || 'none',
    });

    if (!isAuthenticated) {
      navigate(`/cadastro?plano=${selectedPlan}`);
      return;
    }
    if (selectedPlan === 'PLUS' || selectedPlan === 'FREE') {
      userActions.switchPlan(selectedPlan);
    }
  };

  return (
    <PageTemplate showSearchInput={false} alwaysShowLogo>
      <div className="max-w-7xl mx-auto px-4 py-10 space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Planos do Greenbook</h1>
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Busca em documentos oficiais, monografias e IA para suporte à prescrição. Plus está em Early Access gratuito
            até <strong>31/12/2026</strong>.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PlanCard
            title="Free"
            description="Busca textual e filtros essenciais sobre o banco de espécies."
            cta={isAuthenticated && plan === 'FREE' ? 'Plano ativo' : 'Continuar com Free'}
            selected={plan === 'FREE'}
            onClick={() => handleSelectPlan('FREE')}
          />
          <PlanCard
            title="Plus"
            description="Busca semântica nos documentos — devolve o trecho que deu match e a relevância (score). Sem IA."
            helper="Pago a partir de 01/01/2027. Contribuições reservam meses de Plus."
            badge="Early Access • Recomendado"
            cta={isAuthenticated && plan === 'PLUS' ? 'Plus ativo' : 'Ativar Plus agora (gratuito)'}
            selected={plan === 'PLUS'}
            recommended
            onClick={() => handleSelectPlan('PLUS')}
          />
          <PlanCard
            title="Pro"
            description="Plus + busca por IA com sugestões de prescrição (cota mensal de requisições)."
            helper="Em breve. Trials disponíveis via programa de contribuições."
            cta="Em breve"
            disabled
          />
          <PlanCard
            title="Connect"
            description="Pro + integração com ferramentas externas de prescrição (geração e envio de documento)."
            helper="Em desenvolvimento. Contribuidores entram na fila do beta."
            cta="Em breve"
            disabled
          />
        </div>

        <section className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 sm:p-6 space-y-4 bg-white/70 dark:bg-gray-950/20">
          <header className="space-y-1">
            <h2 className="text-2xl sm:text-3xl font-bold leading-tight">
              Early Access encerra em <span className="text-greenbook-light-primary dark:text-greenbook-dark-primary">31/12/2026</span>
            </h2>
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-200">
              Garanta acesso gratuito depois disso contribuindo com a plataforma.
            </p>
          </header>
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Envie feedbacks de erro, melhoria ou conteúdo (fontes bibliográficas e fotos de plantas) — eles são
            contabilizados e desbloqueiam tempo de Plus <strong>para quando o Early Access acabar</strong>. Dúvidas
            não pontuam, mas são respondidas.
          </p>
          {(() => {
            const tiers = [
              { count: 1,  label: '1 mês de Plus + badge Contribuidor' },
              { count: 5,  label: '4 meses totais de Plus' },
              { count: 10, label: '1 ano de Plus + selo Curador + 7 dias de trial Pro' },
              { count: 20, label: '2 anos de Plus + 30 dias de trial Pro' },
              { count: 30, label: '3 anos de Plus + 90 dias de trial Pro' },
              { count: 50, label: 'Plus vitalício + prioridade no beta Connect' },
            ];
            return (
              <>
                <table className="hidden sm:table text-sm w-full border-collapse rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800">
                  <thead>
                    <tr className="text-left bg-greenbook-light-green/40 dark:bg-greenbook-dark-green/40 text-green-900 dark:text-green-100">
                      <th className="py-2 px-3 font-semibold w-40 border-b border-gray-200 dark:border-gray-800">Contribuições</th>
                      <th className="py-2 px-3 font-semibold border-b border-gray-200 dark:border-gray-800">Recompensa</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {tiers.map((t) => (
                      <tr key={t.count}>
                        <td className="py-2 px-3 font-medium">{t.count}</td>
                        <td className="py-2 px-3">{t.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <ul className="sm:hidden rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800">
                  {tiers.map((t) => (
                    <li key={t.count} className="p-3 flex flex-col gap-1">
                      <span className="text-xs uppercase tracking-wide font-semibold text-green-900 dark:text-green-100 bg-greenbook-light-green/40 dark:bg-greenbook-dark-green/40 self-start px-2 py-0.5 rounded">
                        {t.count} contribuiç{t.count === 1 ? 'ão' : 'ões'}
                      </span>
                      <span className="text-sm">{t.label}</span>
                    </li>
                  ))}
                </ul>
              </>
            );
          })()}
          <p className="text-xs text-gray-500">
            Fotos de plantas: 1 ponto por espécie, independente da quantidade de fotos enviadas. Exige declaração de
            autoria.
          </p>
        </section>

        {!isAuthenticated && (
          <div className="text-center text-sm text-gray-700 dark:text-gray-200">
            Já escolheu?{' '}
            <Link
              to="/cadastro"
              className="font-semibold text-greenbook-light-primary hover:underline dark:text-greenbook-dark-primary"
            >
              Ir para cadastro
            </Link>
          </div>
        )}
      </div>
    </PageTemplate>
  );
};

export default Plans;
